import React from 'react';
import { modalStyles as styles } from './modalStyles';
import { colors } from '../theme';

// Dimming data for a device (issue #574). Two sources:
// 1. A per-serial fileset bundled in the app (legacy: product-images/<SN>/fileset/)
// 2. The factory dimming profile for the product family, served by the backend —
//    available for every WE-product even without a per-serial fileset.
export default function DimmingDataModal({ files, profile, serialNumber, onClose }) {
  const hasFiles = files && files.length > 0;

  const downloadProfile = () => {
    if (!profile) return;
    const lines = [
      `# Dimming profile ${profile.profile} (factory default for ${profile.family})`,
      `# Target brightness: ${profile.targetBrightness} cd/m2`,
      serialNumber ? `# Device: ${serialNumber}` : null,
      '# OSD step;Brightness (cd/m2);PWM duty cycle',
      ...profile.steps.map(s => `${s.osd};${s.brightness};${s.dutyCycle}`)
    ].filter(Boolean).join('\n');
    const blob = new Blob([lines], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dimming-profile-${profile.profile}${serialNumber ? `-${serialNumber}` : ''}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2>Dimming Data</h2>
        {hasFiles ? (
          <>
            <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
              Unit-specific calibration fileset on record for this serial number:
            </p>
            <ul style={{ textAlign: 'left', paddingLeft: '1rem' }}>
              {files.map(f => (
                <li key={f.url}>
                  <a href={f.url} target="_blank" rel="noopener noreferrer" download>
                    {f.name}
                  </a>
                </li>
              ))}
            </ul>
          </>
        ) : profile ? (
          <div style={{ textAlign: 'left' }}>
            <p style={{ margin: '0 0 0.3rem', fontWeight: 700 }}>
              Factory dimming profile: {profile.profile}
              <span style={{ color: colors.textMuted, fontWeight: 400 }}>
                {' '}· target {profile.targetBrightness} cd/m²
              </span>
            </p>
            <p style={{ color: colors.textMuted, fontSize: '0.8rem', margin: '0 0 0.6rem' }}>
              {profile.note}
              {profile.variants && profile.variants.length > 0 && (
                <> Variant profiles exist for special builds: {profile.variants.join(', ')} — confirm with engineering if this unit is a customer-specific variant.</>
              )}
            </p>
            <div style={{ maxHeight: '320px', overflowY: 'auto', border: `1px solid ${colors.panelBorder}`, borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, background: colors.surfaceRaised, color: colors.textMuted }}>
                    <th style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>OSD step</th>
                    <th style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>Brightness (cd/m²)</th>
                    <th style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>PWM duty</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.steps.map(step => (
                    <tr key={step.osd} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '0.25rem 0.6rem', textAlign: 'right', fontFamily: 'monospace' }}>{step.osd}</td>
                      <td style={{ padding: '0.25rem 0.6rem', textAlign: 'right' }}>{step.brightness}</td>
                      <td style={{ padding: '0.25rem 0.6rem', textAlign: 'right', color: colors.textMuted }}>{step.dutyCycle}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={downloadProfile} style={{ ...styles.button, marginTop: '0.8rem' }}>
              Download as .txt
            </button>
          </div>
        ) : (
          <p>No dimming data found.</p>
        )}
        <button onClick={onClose} style={styles.button}>Close</button>
      </div>
    </div>
  );
}
